/**
 * Channel constants and series builders for the High Rate tab — the 20 Hz
 * HTS grid stored in `data_gridded_hts` (`scope=grid_data_hts`). The channel
 * set mirrors the server's write-time allow-list DEFAULT_HTS_CHANNEL_LIST
 * (common/cscore/defaults/default_consts.py); the shared time axis is the
 * same `TimeRel_tG00` the standard grid uses, so `buildSeriesPoints` from the
 * Aux Sensor tab works unchanged.
 */
import { AuxPlotSeries, buildSeriesPoints } from '../RunAuxSensor/auxSensorChannels';
import {
  AuxSensorRun, cadenceLabels, SpeedCalibration, wheelSpeedLabels, WheelSpeedLabels,
  windSensorLabel,
} from '../RunAuxSensor/auxSensorLabels';
import { RunHealthObj } from '../../../types/Run';
import { RunWheelLabelSource } from '../../CalibrationView/groundSpeedUtils';

type Gridded = Record<string, (number | null)[]> | null | undefined;

/**
 * Minimum wind5/aero5 raw-stream rate (Hz) for the High Rate tab to be worth
 * showing — above the ~8 Hz ANT page rate, comfortably below the 444 Hz BLE
 * HDR raw stream.
 */
export const HTS_WIND_MIN_HZ = 10;

/**
 * Whether the High Rate tab should be shown. An HTS grid existing
 * (`run.has_hts`) is necessary but not sufficient: a grid can be stale, or
 * triggered by IMU accel alone, holding nothing but low-rate channels
 * interpolated to 20 Hz. The tab shows only when the pipeline's HTS gate
 * actually detected high-rate content (`run_health_obj.hts_sources`): a
 * wind5/aero5 raw stream faster than 10 Hz, or a qualifying per-rev wheel
 * series. The wheel's own conditions — a multi-magnet wheel read natively
 * over BLE, or a sustained faster-than-8-Hz stretch of events — are
 * decided server-side in `hts_source_detect._qualify_wheel`, so
 * `wheel_per_rev` being present is the whole test here; the client no
 * longer needs the run's calibration to second-guess it.
 * Runs processed before `hts_sources` existed need a reprocess to pass.
 */
export function hasHighRateData(
  run: { has_hts?: boolean | null; run_health_obj?: RunHealthObj | null } | null | undefined,
): boolean {
  if (!run?.has_hts) return false;
  const sources = run?.run_health_obj?.hts_sources;
  const windHz = sources?.aero5_raw ?? 0;
  const wheelHz = sources?.wheel_per_rev ?? 0;
  return windHz > HTS_WIND_MIN_HZ || wheelHz > 0;
}

const MS_TO_KMH = 3.6;

export const HTS_CHANNELS = {
  airSpeed: 'AirSpeedRaw_vG31',
  dynPres: 'DynPresRaw_pG31',
  airYaw: 'AirYawRaw_AG11',
  diffs: ['Diff1Raw_pG10', 'Diff2Raw_pG10', 'Diff3Raw_pG10'],
  // Rotational rev/s — safe to plot as-is. WhlLspd_v stays excluded: its
  // units differ by which pipeline path populated the wheel object (GPS
  // fallback vs real wheel sensor), so a fixed client-side conversion
  // would mislabel one of them.
  wheelSpeed: 'WhlRspd_n',
} as const;

/**
 * Per-magnet-pass wheel speed, carried UNGRIDDED in the HTS payload (each
 * series has its own time axis, already rebased to the grid's TimeRel
 * frame). Payload names carry the pipeline level: L30 is the corrected
 * series (precision timestamps + resolved gap angles), L20 the same events
 * under the uniform 1/N-revolution assumption — overlaying the two shows
 * the angular-spacing correction working (or not: residual 3-per-rev
 * sawtooth in the corrected trace means the applied angles are wrong).
 * Only present on multi-magnet runs; payloads written before the L-level
 * rename (2026-08-15) carry the legacy names instead.
 *
 * A run with two speed sensors additionally carries the second (non-
 * primary) sensor's pairs under the `WhlSOAlt` namespace. WHICH physical
 * sensor "Alt" is comes from the run's sensor records (see
 * `wheelSensorLabels`), not the channel name.
 */
export const HTS_WHEEL_EVENT_CHANNELS = {
  corrected: { ts: 'WhlSORspdTs_tL30', value: 'WhlSORspd_nL30' },
  uniform: { ts: 'WhlSORspdTs_tL20', value: 'WhlSORspd_nL20' },
  altCorrected: { ts: 'WhlSOAltRspdTs_tL30', value: 'WhlSOAltRspd_nL30' },
  altUniform: { ts: 'WhlSOAltRspdTs_tL20', value: 'WhlSOAltRspd_nL20' },
  legacyCorrected: { ts: 'WhlRspdEvtTs_t', value: 'WhlRspdEvt_n' },
  legacyUniform: { ts: 'WhlRspdEvtUniTs_t', value: 'WhlRspdEvtUni_n' },
} as const;

/**
 * Every sensor name this tab's plots can use. `wheel` names the per-pass
 * series (its `primary`/`alt` matching the payload's WhlSO / WhlSOAlt
 * namespaces), `wind` the raw probe channels on the Aero sub-tab, and
 * `cadence` the crank sensor's own broadcast series. Each is
 * independently optional: a run can identify its wheels and not its probe,
 * and a series with no name keeps its plain channel-name label.
 */
export interface HtsSensorLabels {
  wheel?: WheelSpeedLabels;
  /** e.g. 'Primary - ID 37197' — a standalone legend entry. */
  wind?: string;
  /** e.g. 'Primary - ID 44349'. */
  cadence?: string;
}

/**
 * Every sensor name this tab's plots can use, resolved from the run's
 * records. All three come from the same intent/actual roster the Aux
 * Sensor tab reads, so a sensor is named identically on both tabs.
 */
export function htsSensorLabels(
  run: (RunWheelLabelSource & AuxSensorRun) | null | undefined,
  calibration?: SpeedCalibration | null,
): HtsSensorLabels {
  return {
    wheel: wheelSpeedLabels(run, calibration),
    wind: windSensorLabel(run)?.label,
    cadence: cadenceLabels(run).primary?.label,
  };
}

/**
 * Pedal-stroke pulse channels (StrokePulseProcessor). The G20 ripple is
 * FITTED to the per-pass wheel data (variable size, zero while coasting;
 * falls back to the constant-fraction model when no fit exists) and the
 * matched power ripple is its work-rate twin. The CoG ripple is the
 * combined-mass view: the bike frame surges semi-decoupled from the
 * rider, so it has the power-model amplitude (smaller) on the fitted
 * phase unrotated (measured bike-vs-mass shift 0° — frame and centre
 * of mass ripple in phase). `wheelRecon` = fit trend + fitted ripple
 * (the bicycle); `wheelMass` = crank-locked one-revolution boxcar trend
 * + CoG ripple (WhlRspdMass_nG40, the combined-mass speed). The
 * ungridded residual is the per-magnet-pass wheel speed after detrend;
 * the per-window series carry the coherent-demodulation output. Only
 * present on runs where the analysis ran (multi-magnet BLE per-pass
 * series + crank + power).
 */
export const HTS_STROKE_CHANNELS = {
  rippleFitted: 'StrokeRspdPulse_nG20',
  rippleCog: 'StrokeRspdPulse_nG30',
  powerRipple: 'StrokePwrPulse_PG20',
  wheelRecon: 'WhlRspd_nG30',
  // The combined-mass speed: the CoG ripple in phase with the bike
  // (measured ∠H ≈ 0) on a crank-locked one-revolution boxcar trend
  // that nulls the whole cadence comb.
  wheelMass: 'WhlRspdMass_nG40',
  // The L40 interpolated bicycle speed (own time axis): exact at every
  // per-pass L30 point, with a harmonic prior at 2x crank phase
  // synthesising 16 points per pulse cycle between the passes. The
  // combined-mass twin shares the same time axis and carrier, with the
  // rigid-body ripple (3 s power, fraction 0.64) in phase with the
  // bike's own harmonic (measured bike-vs-mass shift 0°).
  wheelFit: { ts: 'WhlSORspdTs_tL40', value: 'WhlSORspd_nL40' },
  wheelMassFit: { ts: 'WhlSORspdTs_tL40', value: 'WhlSORspdMass_nL40' },
  // Crank phase on the same axis, anchored by the measured velocity
  // harmonic: ripple peaks (cranks horizontal) at 90/270 mod 360, dead
  // spots at 0/180. CrankPhase is the MONOTONIC cumulative degrees
  // (referenced by name, not plotted); CrankAngle is its 0-360 wrap —
  // the plotted angle; CrankCadDerived is the phase channel's own rate
  // over 360 (rev/s). NaN (a line gap) where indeterminate — coasts
  // and phase dropouts.
  crankPhase: { ts: 'WhlSORspdTs_tL40', value: 'CrankPhase_AL40' },
  crankAngle: { ts: 'WhlSORspdTs_tL40', value: 'CrankAngle_AL40' },
  // The strict-period twins: the angle advancing exactly with the
  // fused carrier (one fixed anchor per pedalling stretch) and the
  // wheel speed with its ripple re-projected onto that locked phase —
  // L40 minus L50 is the cycle-to-cycle phase deviation.
  wheelLocked: { ts: 'WhlSORspdTs_tL40', value: 'WhlSORspd_nL50' },
  crankAngleLocked: { ts: 'WhlSORspdTs_tL40', value: 'CrankAngle_AL50' },
  cadDerived: { ts: 'WhlSORspdTs_tL40', value: 'CrankCadDerived_RL40' },
  // The fused crank rate: ripple-derived where the envelope is strong,
  // broadcast cadence where it fades, pinned per stretch to the rev
  // counter. Its integral is the second-pass carrier the crank-phase
  // channels were demodulated against.
  cadFused: { ts: 'WhlSORspdTs_tL40', value: 'CrankCadDerived_RL50' },
  // The sensor's own broadcast cadence (rev/s), ungridded on its own
  // rebased axis — the native series the derived one is compared to.
  cadNative: { ts: 'CrankCadTs_tL20', value: 'CrankCad_RL20' },
  resid: { ts: 'StrokeResidTs_t', value: 'StrokeResid_n' },
  windowTs: 'StrokeWinTs_t',
  windowAmp: 'StrokeWinAmp_n',
  windowAmpPred: 'StrokeWinAmpPred_n',
} as const;

/**
 * Points for an event-level series carrying its own time channel — unlike
 * `buildSeriesPoints`, which pins x to the shared grid axis.
 */
export function buildEventSeriesPoints(
  hts: Gridded,
  tsChannel: string,
  valueChannel: string,
): [number, number | null][] {
  if (!hts) return [];
  const time = hts[tsChannel];
  const values = hts[valueChannel];
  if (!time || !values) return [];
  const n = Math.min(time.length, values.length);
  const points: [number, number | null][] = [];
  for (let i = 0; i < n; i += 1) {
    const t = time[i];
    const v = values[i];
    if (t == null || Number.isNaN(t)) continue;
    points.push([t, v == null || Number.isNaN(v) ? null : v]);
  }
  return points;
}

/**
 * Points for the difference of two value channels sharing one time axis —
 * used to show the bicycle-minus-mass relative ripple at its own scale.
 */
export function buildEventDiffPoints(
  hts: Gridded,
  tsChannel: string,
  valueChannel: string,
  minusChannel: string,
): [number, number | null][] {
  if (!hts) return [];
  const time = hts[tsChannel];
  const values = hts[valueChannel];
  const minus = hts[minusChannel];
  if (!time || !values || !minus) return [];
  const n = Math.min(time.length, values.length, minus.length);
  const points: [number, number | null][] = [];
  for (let i = 0; i < n; i += 1) {
    const t = time[i];
    const v = values[i];
    const m = minus[i];
    if (t == null || Number.isNaN(t)) continue;
    const bad = v == null || m == null || Number.isNaN(v) || Number.isNaN(m);
    points.push([t, bad ? null : v - m]);
  }
  return points;
}

/**
 * First non-empty event series from a list of channel-name pairs — used to
 * read the L-level payload names with fallback to the legacy names on runs
 * processed before the rename.
 */
export function buildEventSeriesPointsWithFallback(
  hts: Gridded,
  pairs: readonly { readonly ts: string; readonly value: string }[],
): [number, number | null][] {
  for (const pair of pairs) {
    const points = buildEventSeriesPoints(hts, pair.ts, pair.value);
    if (points.length > 0) return points;
  }
  return [];
}

/**
 * Point budget per plotted series. 20 Hz over a ~8-minute run is ~9k points
 * per channel; SVG ApexCharts stays responsive around this budget, and the
 * min/max decimation below preserves the spikes that matter in raw aero data.
 */
export const HTS_MAX_POINTS = 4000;

/**
 * Bucketed min/max decimation: each bucket contributes its extreme samples
 * (in time order), so peaks and troughs survive that a plain stride or mean
 * would flatten. Buckets that are entirely gaps contribute one null point to
 * keep the gap visible. Input under budget is returned untouched (referential
 * stability for `useMemo` consumers).
 */
export function downsampleMinMax(
  points: [number, number | null][],
  maxPoints: number = HTS_MAX_POINTS,
): [number, number | null][] {
  if (points.length <= maxPoints) return points;
  const bucketCount = Math.max(1, Math.floor(maxPoints / 2));
  const bucketSize = Math.ceil(points.length / bucketCount);
  const out: [number, number | null][] = [];
  for (let start = 0; start < points.length; start += bucketSize) {
    const end = Math.min(start + bucketSize, points.length);
    let minPoint: [number, number] | undefined;
    let maxPoint: [number, number] | undefined;
    for (let i = start; i < end; i += 1) {
      const value = points[i][1];
      if (value == null) continue;
      if (minPoint === undefined || value < minPoint[1]) minPoint = points[i] as [number, number];
      if (maxPoint === undefined || value > maxPoint[1]) maxPoint = points[i] as [number, number];
    }
    if (minPoint === undefined || maxPoint === undefined) {
      out.push([points[start][0], null]);
      continue;
    }
    const [first, second] = minPoint[0] <= maxPoint[0] ? [minPoint, maxPoint] : [maxPoint, minPoint];
    out.push(first);
    if (second !== first) out.push(second);
  }
  return out;
}

function series(
  hts: Gridded,
  key: string,
  label: string,
  channel: string,
  transform?: (v: number) => number,
): AuxPlotSeries {
  return { key, label, points: downsampleMinMax(buildSeriesPoints(hts, channel, { transform })) };
}

/** The High Rate sub-tab a plot belongs to. */
export type HtsPlotGroup = 'aero' | 'mechanical';

export interface HtsPlotSpec {
  title: string;
  unit: string;
  group: HtsPlotGroup;
  /** Render per-series visibility checkboxes above the chart. */
  selectable?: boolean;
  /** Right-hand y-axis for series flagged `secondaryAxis` (AuxSensorPlot prop). */
  secondaryAxis?: { title: string; unit: string; min?: number; max?: number; tickAmount?: number };
  /** `labels` names each series after the device it came from. */
  build: (hts: Gridded, labels?: HtsSensorLabels) => AuxPlotSeries[];
}

const toKmh = (v: number) => v * MS_TO_KMH;

/**
 * The tab's plots, in display order within each sub-tab (Aero /
 * Mechanical). One descriptor per plot keeps the title/unit next to the
 * channels they describe, and lets RunHighRate render the tab by mapping
 * over this list instead of naming every plot twice.
 */
export const HTS_PLOTS: HtsPlotSpec[] = [
  // Aero tab, refined -> raw: yaw and speed are derived from the
  // pressures, so the most-processed quantity sits on top and the raw
  // probe readings at the bottom.
  {
    title: 'Air Yaw (Raw)',
    unit: '°',
    group: 'aero',
    build: (hts, labels) => [
      series(hts, 'hts-air-yaw', labels?.wind ?? 'Air Yaw (Raw)', HTS_CHANNELS.airYaw),
    ],
  },
  {
    // Air speed is m/s at source, plotted in km/h.
    title: 'Air Speed (Raw)',
    unit: 'km/h',
    group: 'aero',
    build: (hts, labels) => [
      series(hts, 'hts-air-speed', labels?.wind ?? 'Air Speed (Raw)', HTS_CHANNELS.airSpeed, toKmh),
    ],
  },
  {
    title: 'Dynamic Pressure (Raw)',
    unit: 'Pa',
    group: 'aero',
    build: (hts, labels) => [
      series(hts, 'hts-dyn-pres', labels?.wind ?? 'Dynamic Pressure (Raw)', HTS_CHANNELS.dynPres),
    ],
  },
  {
    // The three raw differential pressures of the probe.
    title: 'Differential Pressures (Raw)',
    unit: 'Pa',
    group: 'aero',
    // Deliberately NOT sensor-named: all three are ports of ONE probe, so
    // the name would repeat three times across a single legend, and the
    // other three Aero plots already say which probe this sub-tab is.
    build: (hts) => HTS_CHANNELS.diffs.map(
      (channel, i) => series(hts, `hts-diff-${i + 1}`, `Diff ${i + 1}`, channel),
    ),
  },
  {
    // Crank cadence in rev/s: the sensor's own broadcast series against
    // the cadence derived from the frequency of the L40 crank-phase
    // angles — the two agree when the phase anchoring is healthy, and
    // the derived trace additionally resolves within-stretch variation
    // between broadcasts.
    title: 'Cadence',
    unit: 'rev/s',
    group: 'mechanical',
    selectable: true,
    build: (hts, labels) => {
      const out: AuxPlotSeries[] = [];
      const native = buildEventSeriesPoints(
        hts, HTS_STROKE_CHANNELS.cadNative.ts, HTS_STROKE_CHANNELS.cadNative.value);
      if (native.length > 0) {
        out.push({
          key: 'hts-cad-native',
          // The only series here a sensor broadcast — the other two are
          // derived from the crank phase, so they stay channel-named.
          label: `${labels?.cadence ?? 'Cadence'} (CrankCad_RL20)`,
          points: downsampleMinMax(native),
        });
      }
      const derived = buildEventSeriesPoints(
        hts, HTS_STROKE_CHANNELS.cadDerived.ts, HTS_STROKE_CHANNELS.cadDerived.value);
      if (derived.length > 0) {
        out.push({
          key: 'hts-cad-derived',
          label: 'Phase derived (CrankCadDerived_RL40)',
          points: downsampleMinMax(derived, 3 * HTS_MAX_POINTS),
        });
      }
      const fusedSeries = buildEventSeriesPoints(
        hts, HTS_STROKE_CHANNELS.cadFused.ts, HTS_STROKE_CHANNELS.cadFused.value);
      if (fusedSeries.length > 0) {
        out.push({
          key: 'hts-cad-fused',
          label: 'Fused carrier rate (CrankCadDerived_RL50)',
          points: downsampleMinMax(fusedSeries, 3 * HTS_MAX_POINTS),
        });
      }
      return out;
    },
  },
  {
    // Rotational wheel speed in rev/s: the measured per-magnet passes as
    // bare points, threaded by the L40 interpolated bicycle curve — which
    // passes EXACTLY through every measured point, with a harmonic prior
    // at 2x crank phase shaping the gaps — and the combined-mass twin
    // (the bicycle curve with its own harmonic swapped for the modelled
    // CoG ripple: v_bike − r_bike + r_mass). Runs processed before L40
    // existed fall back to the lock-in reconstruction, and before the
    // stroke streams to the plain gridded channel.
    title: 'Wheel Speed (Rotational)',
    unit: 'rev/s',
    group: 'mechanical',
    selectable: true,
    // Fixed 0-360 span with ticks on the quarter points, so the 90/270
    // peak convention reads straight off the gridlines.
    secondaryAxis: { title: 'Crank Phase', unit: '°', min: 0, max: 360, tickAmount: 4 },
    build: (hts, labels) => {
      const out: AuxPlotSeries[] = [];
      const measuredPoints = buildEventSeriesPointsWithFallback(hts, [
        HTS_WHEEL_EVENT_CHANNELS.corrected, HTS_WHEEL_EVENT_CHANNELS.legacyCorrected,
      ]);
      if (measuredPoints.length > 0) {
        out.push({
          key: 'hts-wheel-evt-points',
          label: labels?.wheel?.primary ? `Measured — ${labels.wheel.primary.label}` : 'Measured (WhlSORspd_nL30)',
          markersOnly: true,
          points: downsampleMinMax(measuredPoints),
        });
      }
      const altMeasured = buildEventSeriesPoints(
        hts, HTS_WHEEL_EVENT_CHANNELS.altCorrected.ts, HTS_WHEEL_EVENT_CHANNELS.altCorrected.value);
      if (altMeasured.length > 0) {
        out.push({
          key: 'hts-wheel-evt-points-alt',
          label: labels?.wheel?.alt ? `Measured — ${labels.wheel.alt.label}` : 'Measured — 2nd sensor (WhlSOAltRspd_nL30)',
          markersOnly: true,
          points: downsampleMinMax(altMeasured),
        });
      }
      const fitPoints = buildEventSeriesPoints(
        hts, HTS_STROKE_CHANNELS.wheelFit.ts, HTS_STROKE_CHANNELS.wheelFit.value);
      if (fitPoints.length > 0) {
        // ~4 points per pass: the shared budget's min/max decimation would
        // strip the synthesised intra-gap points, so give the curve 3x.
        out.push({
          key: 'hts-stroke-whl-l40',
          label: 'Bicycle fit (WhlSORspd_nL40)',
          points: downsampleMinMax(fitPoints, 3 * HTS_MAX_POINTS),
        });
      } else {
        const recon = series(
          hts, 'hts-stroke-whl-recon', 'Bicycle (trend + StrokeRspdPulse_nG20)',
          HTS_STROKE_CHANNELS.wheelRecon,
        );
        if (recon.points.length > 0) out.push(recon);
      }
      const massFitPoints = buildEventSeriesPoints(
        hts, HTS_STROKE_CHANNELS.wheelMassFit.ts, HTS_STROKE_CHANNELS.wheelMassFit.value);
      if (massFitPoints.length > 0) {
        out.push({
          key: 'hts-stroke-whl-mass-l40',
          label: 'Combined mass (WhlSORspdMass_nL40)',
          points: downsampleMinMax(massFitPoints, 3 * HTS_MAX_POINTS),
        });
      }
      const mass = series(
        hts, 'hts-stroke-whl-mass-g40', 'Combined mass (WhlRspdMass_nG40)',
        HTS_STROKE_CHANNELS.wheelMass,
      );
      if (mass.points.length > 0) out.push(mass);
      const lockedPoints = buildEventSeriesPoints(
        hts, HTS_STROKE_CHANNELS.wheelLocked.ts, HTS_STROKE_CHANNELS.wheelLocked.value);
      if (lockedPoints.length > 0) {
        out.push({
          key: 'hts-stroke-whl-l50',
          label: 'Strict-period fit (WhlSORspd_nL50)',
          points: downsampleMinMax(lockedPoints, 3 * HTS_MAX_POINTS),
        });
      }
      const crankPoints = buildEventSeriesPoints(
        hts, HTS_STROKE_CHANNELS.crankAngle.ts, HTS_STROKE_CHANNELS.crankAngle.value);
      if (crankPoints.length > 0) {
        out.push({
          key: 'hts-stroke-crank-angle',
          label: 'Crank angle (CrankAngle_AL40)',
          secondaryAxis: true,
          points: downsampleMinMax(crankPoints, 3 * HTS_MAX_POINTS),
        });
      }
      const lockedAngle = buildEventSeriesPoints(
        hts, HTS_STROKE_CHANNELS.crankAngleLocked.ts, HTS_STROKE_CHANNELS.crankAngleLocked.value);
      if (lockedAngle.length > 0) {
        out.push({
          key: 'hts-stroke-crank-angle-l50',
          label: 'Strict crank angle (CrankAngle_AL50)',
          secondaryAxis: true,
          points: downsampleMinMax(lockedAngle, 3 * HTS_MAX_POINTS),
        });
      }
      if (out.length === 0) {
        return [series(hts, 'hts-wheel-speed', 'Wheel Speed', HTS_CHANNELS.wheelSpeed)];
      }
      return out;
    },
  },
  {
    // The angular-spacing correction, per magnet pass: the same events
    // under the uniform 1/N assumption vs with the applied gap angles.
    // A flat corrected trace over a sawtoothed uniform trace = the
    // correction worked; sawtooth surviving in the corrected trace = the
    // applied angles (or their phase rotation) are wrong. Two-sensor runs
    // carry the second sensor's pair too, named by its sensor id.
    title: 'Wheel Speed — Spacing Correction (per pass)',
    unit: 'rev/s',
    group: 'mechanical',
    selectable: true,
    build: (hts, labels) => {
      const out: AuxPlotSeries[] = [];
      const uniform = buildEventSeriesPointsWithFallback(hts, [
        HTS_WHEEL_EVENT_CHANNELS.uniform, HTS_WHEEL_EVENT_CHANNELS.legacyUniform,
      ]);
      if (uniform.length > 0) {
        out.push({
          key: 'hts-wheel-evt-uniform',
          label: labels?.wheel?.primary
            ? `Uniform — ${labels.wheel.primary.label}` : 'Uniform spacing (WhlSORspd_nL20)',
          points: downsampleMinMax(uniform),
        });
      }
      const corrected = buildEventSeriesPointsWithFallback(hts, [
        HTS_WHEEL_EVENT_CHANNELS.corrected, HTS_WHEEL_EVENT_CHANNELS.legacyCorrected,
      ]);
      if (corrected.length > 0) {
        out.push({
          key: 'hts-wheel-evt-corrected',
          label: labels?.wheel?.primary
            ? `Corrected — ${labels.wheel.primary.label}` : 'Spacing corrected (WhlSORspd_nL30)',
          points: downsampleMinMax(corrected),
        });
      }
      const altUniform = buildEventSeriesPoints(
        hts, HTS_WHEEL_EVENT_CHANNELS.altUniform.ts, HTS_WHEEL_EVENT_CHANNELS.altUniform.value);
      if (altUniform.length > 0) {
        out.push({
          key: 'hts-wheel-evt-uniform-alt',
          label: labels?.wheel?.alt
            ? `Uniform — ${labels.wheel.alt.label}` : 'Uniform — 2nd sensor (WhlSOAltRspd_nL20)',
          points: downsampleMinMax(altUniform),
        });
      }
      const altCorrected = buildEventSeriesPoints(
        hts, HTS_WHEEL_EVENT_CHANNELS.altCorrected.ts, HTS_WHEEL_EVENT_CHANNELS.altCorrected.value);
      if (altCorrected.length > 0) {
        out.push({
          key: 'hts-wheel-evt-corrected-alt',
          label: labels?.wheel?.alt
            ? `Corrected — ${labels.wheel.alt.label}` : 'Corrected — 2nd sensor (WhlSOAltRspd_nL30)',
          points: downsampleMinMax(altCorrected),
        });
      }
      return out;
    },
  },
  {
    // The relative pedal-stroke ripple at its own scale: the L40 bicycle
    // curve minus its combined-mass twin. The two curves share the same
    // measured carrier and differ only in their 2x-cadence harmonics, so
    // the difference is trend-free by construction — zero-centred, no
    // moving-average leakage, no acceleration artifacts. What remains is
    // the bike's surge relative to the centre of gravity: the bicycle
    // harmonic minus the in-phase rigid-body ripple: the frame's
    // amplitude excess. Runs processed before the mass channel fall
    // back to the lock-in view (per-pass residual + lock-in ripples).
    title: 'Pedal Stroke — Speed Ripple',
    unit: 'rev/s',
    group: 'mechanical',
    selectable: true,
    build: (hts) => {
      const diff = buildEventDiffPoints(
        hts, HTS_STROKE_CHANNELS.wheelFit.ts, HTS_STROKE_CHANNELS.wheelFit.value,
        HTS_STROKE_CHANNELS.wheelMassFit.value);
      if (diff.length > 0) {
        return [{
          key: 'hts-stroke-ripple-rel',
          label: 'Bicycle − combined mass (WhlSORspd_nL40 − WhlSORspdMass_nL40)',
          points: downsampleMinMax(diff, 3 * HTS_MAX_POINTS),
        }];
      }
      return [
        {
          key: 'hts-stroke-resid',
          label: 'Per-pass residual',
          points: downsampleMinMax(buildEventSeriesPoints(
            hts, HTS_STROKE_CHANNELS.resid.ts, HTS_STROKE_CHANNELS.resid.value)),
        },
        series(hts, 'hts-stroke-fit', 'Fitted ripple (bike)', HTS_STROKE_CHANNELS.rippleFitted),
        series(hts, 'hts-stroke-cog', 'Combined-mass ripple', HTS_STROKE_CHANNELS.rippleCog),
      ];
    },
  },
];

/** A plot is worth rendering only if some series has a real (non-null) point. */
export function plotHasData(seriesList: AuxPlotSeries[]): boolean {
  return seriesList.some((s) => s.points.some(([, v]) => v != null));
}
